import { NavLink, Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Calculator, FileText, Settings, LogOut, ChevronDown, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { useTheme } from "@/context/ThemeContext";
import { CompanySwitcher } from "@/components/CompanySwitcher";
import { DeadlineBanner } from "@/components/DeadlineBanner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { YEARS } from "@/lib/format";

const NAV = [
  {
    group: "Pilotage",
    items: [
      { to: "/", label: "Tableau de bord", icon: LayoutDashboard, id: "nav-dashboard" },
      { to: "/simulateur", label: "Simulateur", icon: Calculator, id: "nav-calculator" },
    ],
  },
  {
    group: "Dossier",
    items: [
      { to: "/salaries", label: "Salariés", icon: Users, id: "nav-employees" },
      {
        to: "/declarations", label: "Déclarations", icon: FileText, id: "nav-declarations",
        children: [
          { to: "/declarations", label: "Toutes les déclarations", id: "nav-declarations-all" },
          { to: "/declarations?type=acomptes", label: "Acomptes", tag: "2501-SD", id: "nav-declarations-2501" },
          { to: "/declarations?type=annuelle", label: "Liquidation annuelle", tag: "2502-SD", id: "nav-declarations-2502" },
        ],
      },
    ],
  },
  {
    group: "Configuration",
    items: [
      {
        to: "/parametres", label: "Paramètres", icon: Settings, id: "nav-settings",
        children: [
          { to: "/parametres", label: "Entreprise active", id: "nav-settings-company" },
          { to: "/parametres?new=1", label: "Nouvelle entreprise", id: "nav-settings-new" },
        ],
      },
    ],
  },
];

const NavItem = ({ item }) => {
  const { pathname, search } = useLocation();
  const open = pathname === item.to || pathname.startsWith(`${item.to}/`);
  return (
    <div>
      <NavLink to={item.to} end={item.to === "/"} data-testid={item.id} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
        <item.icon className="h-4 w-4" />
        <span className="flex-1">{item.label}</span>
        {item.children && <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : "opacity-50"}`} />}
      </NavLink>
      {item.children && open && (
        <div className="nav-sub fade-up" data-testid={`${item.id}-submenu`}>
          {item.children.map((c) => {
            const active = `${pathname}${search}` === c.to;
            return (
              <Link key={c.to} to={c.to} data-testid={c.id} className={`nav-sub-link ${active ? "active" : ""}`}>
                <span>{c.label}</span>
                {c.tag && <span className="nav-tag">{c.tag}</span>}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { year, setYear, company } = useCompany();
  const { isDark, toggle } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 bg-ink text-slate-200 flex flex-col fixed inset-y-0 z-20 border-r border-ink-border" data-testid="sidebar">
        <div className="px-5 py-4 border-b border-ink-border flex items-center gap-3">
          <div className="h-11 w-11 rounded-md bg-white flex items-center justify-center p-1 shrink-0">
            <img src="/jn-logo.png" alt="JN Expertise" className="h-full w-full object-contain" data-testid="brand-logo" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-white leading-tight tracking-tight">JN Expertise</div>
            <div className="text-[11px] text-brand-300 tracking-wide truncate">TaxeSalaires · 2501 / 2502</div>
          </div>
        </div>
        <div className="px-4 py-4 border-b border-ink-border">
          <div className="eyebrow text-brand-300/60 mb-2">Entreprise active</div>
          <CompanySwitcher />
        </div>
        <nav className="flex-1 px-3 pb-4 overflow-y-auto">
          {NAV.map((g) => (
            <div key={g.group}>
              <div className="nav-group-label">{g.group}</div>
              <div className="space-y-1">{g.items.map((item) => <NavItem key={item.to} item={item} />)}</div>
            </div>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-ink-border">
          <div className="text-sm text-white font-medium truncate" data-testid="user-name">{user?.name}</div>
          <div className="text-xs text-slate-400 truncate">{user?.email}</div>
          <Button variant="ghost" size="sm" className="mt-3 w-full justify-start text-slate-300 hover:text-white hover:bg-ink-light" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>
      <main className="flex-1 ml-64 min-h-screen">
        <header className="sticky top-0 z-10 bg-card/80 backdrop-blur-md border-b border-border border-t-2 border-t-brand-500 px-8 h-14 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {company ? (
              <span data-testid="header-company-name"><span className="font-semibold text-foreground">{company.name}</span>{company.siret && <span className="ml-2 font-mono text-xs text-muted-foreground">SIRET {company.siret}</span>}</span>
            ) : <span>Aucune entreprise sélectionnée</span>}
          </div>
          <div className="flex items-center gap-3">
            <span className="eyebrow">Exercice</span>
            <Select value={String(year)} onValueChange={setYear}>
              <SelectTrigger className="w-28 h-9 font-mono" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => <SelectItem key={y} value={String(y)} data-testid={`year-option-${y}`}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={toggle} aria-label={isDark ? "Mode clair" : "Mode sombre"} data-testid="theme-toggle">
              {isDark ? <Sun className="h-4 w-4 text-brand-300" /> : <Moon className="h-4 w-4 text-brand-600" />}
            </Button>
          </div>
        </header>
        <DeadlineBanner />
        <div className="p-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
